import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  UncontrolledTooltip
} from 'reactstrap';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { fetchCatalogLots, fetchSelectedLotsData, submitSelectedBids } from '../../modules/bulkBidding';

import DebouncedSearchInput from './DebouncedSearchInput';
import LotBidsDetailModal from './LotBidsDetailModal';
import { BID_TYPES, getLastRefreshedTime } from './utils';

const Header = ({ onSearchStringChange }) => {
  const { catalogRef } = useParams();
  const dispatch = useDispatch();

  const {
    selectedBids,
    saleInfo: { saleName, saleDate, lotCount },
    placedLotsData,
    submitBidsData,
    apiError,
    catalogLots
  } = useSelector(state => state.bulkBidding);

  const [isPlaceNewBidModalOpen, setPlaceNewBidModalOpen] = useState(false);
  const [placeBidButtonLoading, setPlaceBidButtonLoading] = useState(false);
  const [bidType, setBidType] = useState(BID_TYPES.MAX_BID);

  const selectedBidsCount = Object.keys(selectedBids).length;
  const lastRefreshedTimed = getLastRefreshedTime();
  const placeButtonTitle = selectedBidsCount ? `Place ${selectedBidsCount} New ${bidType === BID_TYPES.MAX_BID ? 'Max' : 'Current'} Bids` : 'Place New Bids';

  useEffect(() => {
    if (placedLotsData.length) {
      setPlaceNewBidModalOpen(true);
      setPlaceBidButtonLoading(false);
    }
  }, [placedLotsData]);

  useEffect(() => {
    if (submitBidsData.successBids.length || submitBidsData.failedBids.length || apiError) {
      setPlaceNewBidModalOpen(false);
    }
  }, [submitBidsData, apiError]);

  const confirmBidHandler = () => {
    const bidAdminOrderBids = Object.keys(selectedBids).map((key) => ({
      bidAmount: selectedBids[key],
      lotRef: key
    }));
    dispatch(submitSelectedBids({
      bidAdminOrderBids,
      catalogRef,
      bidType,
    }, placedLotsData));
  };

  return (
    <Row>
      {isPlaceNewBidModalOpen && (
      <LotBidsDetailModal
        bidTitle={placeButtonTitle}
        data={placedLotsData}
        confirmBidHandler={confirmBidHandler}
        onClose={() => setPlaceNewBidModalOpen(false)}
      />
      )}
      <Col xs={4} sm={4}>
        <h1>{saleName}</h1>
        <h5 className="my-3">{saleDate}</h5>
        <DebouncedSearchInput
          onChange={onSearchStringChange}
          disabled={catalogLots.length !== lotCount}
          placeholder="Search by lot number, description or consigner"
        />
      </Col>
      <Col xs={8} sm={8} className="align-right bidSection">
        <h5>
          {lastRefreshedTimed}
          <Button color="link" onClick={() => dispatch(fetchCatalogLots(catalogRef, lotCount))}>Refresh</Button>
        </h5>
        <FormGroup check className="my-3">
          <Label check className="pr-5">
            <Input
              type="radio"
              name="saleTypeRadio"
              className="mt-1"
              checked={bidType === BID_TYPES.MAX_BID}
              onChange={() => setBidType(BID_TYPES.MAX_BID)}
            />
            <span>Max Bid</span>
          </Label>
          <Label>
            <Input
              type="radio"
              name="saleTypeRadio"
              checked={bidType === BID_TYPES.CURRENT_BID}
              className="mt-1"
              onChange={() => setBidType(BID_TYPES.CURRENT_BID)}
            />
            <span>Current Bid</span>
          </Label>
          <FontAwesomeIcon icon="question-circle" id="bulkbidding-question" className="ml-2" color="#9C9C9C" />
          <UncontrolledTooltip placement="bottom" target="bulkbidding-question">
            Move the current bid for the lot to the amount that you specify
          </UncontrolledTooltip>
        </FormGroup>
        <Button
          color="primary"
          className="cursor-not-allowed placeBidbutton"
          disabled={!selectedBidsCount || placeBidButtonLoading}
          onClick={() => {
            setPlaceBidButtonLoading(true);
            dispatch(fetchSelectedLotsData(catalogRef, Object.keys(selectedBids)));
          }}
        >
          <span>{placeButtonTitle}</span>
          {placeBidButtonLoading && <ReactLoading className="loading-spinner" type="spin" height="auto" width={15} />}
        </Button>
      </Col>
    </Row>
  );
};

export default Header;
